import * as z from 'zod';

export const CreatePickupLocationRequest = z.object({
  pickupLocation: z
    .object({
      _id: z
        .string()
        .describe('PickupLocation ID')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Represents the time this PickupLocation was created')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Represents the time this PickupLocation was last updated')
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Pickup Location Name')
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      address: z
        .object({
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
          addressLine1: z.string().optional().nullable(),
          addressLine2: z.string().optional().nullable(),
        })
        .describe('Pickup Location Address'),
      deliveryTime: z
        .string()
        .describe(
          'Expected delivery time in free text. For example, `"3-5 business days"`.'
        )
        .max(500)
        .optional()
        .nullable(),
      instructions: z
        .string()
        .describe(
          'Instructions for carrier. For example, `"Please knock on the door. If unanswered, please call contact number. Thanks."`.'
        )
        .max(1000)
        .optional()
        .nullable(),
      active: z
        .boolean()
        .describe('inactive pickup locations should not be shown in checkout')
        .optional()
        .nullable(),
      rates: z
        .array(
          z.object({
            amount: z
              .string()
              .describe(
                'there is an AND logic between all the conditions. Empty conditions means true.\nThe amount of the rate that will be returned if all conditions are met.'
              )
              .optional(),
            conditions: z
              .array(
                z.object({
                  type: z
                    .enum([
                      'BY_TOTAL_WEIGHT',
                      'BY_TOTAL_PRICE',
                      'BY_TOTAL_QUANTITY',
                    ])
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'The value in respective to the condition type\nWeight values should be in the same weight units of the store: KG / LB\nTotal price is according to the store currency\nQuantity of items should be integers'
                    )
                    .optional(),
                  operator: z.enum(['EQ', 'GT', 'GTE', 'LT', 'LTE']).optional(),
                })
              )
              .min(0)
              .max(10)
              .optional(),
            multiplyByQuantity: z
              .boolean()
              .describe(
                'When this flag is set to true, multiply the amount by the number of line items passed on the request.'
              )
              .optional(),
          })
        )
        .max(50)
        .optional(),
      deliveryRegionIds: z.array(z.string()).max(50).optional(),
    })
    .describe('PickupLocation to be created'),
});
export const CreatePickupLocationResponse = z.object({
  _id: z
    .string()
    .describe('PickupLocation ID')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Represents the time this PickupLocation was created')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Represents the time this PickupLocation was last updated')
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Pickup Location Name')
    .min(1)
    .max(100)
    .optional()
    .nullable(),
  address: z
    .object({
      city: z.string().optional().nullable(),
      subdivision: z.string().optional().nullable(),
      country: z.string().optional().nullable(),
      postalCode: z.string().optional().nullable(),
      addressLine1: z.string().optional().nullable(),
      addressLine2: z.string().optional().nullable(),
    })
    .describe('Pickup Location Address')
    .optional(),
  deliveryTime: z
    .string()
    .describe(
      'Expected delivery time in free text. For example, `"3-5 business days"`.'
    )
    .max(500)
    .optional()
    .nullable(),
  instructions: z
    .string()
    .describe(
      'Instructions for carrier. For example, `"Please knock on the door. If unanswered, please call contact number. Thanks."`.'
    )
    .max(1000)
    .optional()
    .nullable(),
  active: z
    .boolean()
    .describe('inactive pickup locations should not be shown in checkout')
    .optional()
    .nullable(),
  rates: z
    .array(
      z.object({
        amount: z
          .string()
          .describe(
            'there is an AND logic between all the conditions. Empty conditions means true.\nThe amount of the rate that will be returned if all conditions are met.'
          )
          .optional(),
        conditions: z
          .array(
            z.object({
              type: z
                .enum([
                  'BY_TOTAL_WEIGHT',
                  'BY_TOTAL_PRICE',
                  'BY_TOTAL_QUANTITY',
                ])
                .optional(),
              value: z
                .string()
                .describe(
                  'The value in respective to the condition type\nWeight values should be in the same weight units of the store: KG / LB\nTotal price is according to the store currency\nQuantity of items should be integers'
                )
                .optional(),
              operator: z
                .enum(['EQ', 'GT', 'GTE', 'LT', 'LTE'])
                .describe('Logical operator.')
                .optional(),
            })
          )
          .min(0)
          .max(10)
          .optional(),
        multiplyByQuantity: z
          .boolean()
          .describe(
            'When this flag is set to true, multiply the amount by the number of line items passed on the request.'
          )
          .optional(),
      })
    )
    .max(50)
    .optional(),
  deliveryRegionIds: z.array(z.string()).max(50).optional(),
});
export const GetPickupLocationRequest = z.object({
  pickupLocationId: z
    .string()
    .describe('Id of the PickupLocation to retrieve')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetPickupLocationResponse = z.object({
  _id: z
    .string()
    .describe('PickupLocation ID')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Represents the time this PickupLocation was created')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Represents the time this PickupLocation was last updated')
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Pickup Location Name')
    .min(1)
    .max(100)
    .optional()
    .nullable(),
  address: z
    .object({
      city: z.string().optional().nullable(),
      subdivision: z.string().optional().nullable(),
      country: z.string().optional().nullable(),
      postalCode: z.string().optional().nullable(),
      addressLine1: z.string().optional().nullable(),
      addressLine2: z.string().optional().nullable(),
    })
    .describe('Pickup Location Address')
    .optional(),
  deliveryTime: z
    .string()
    .describe(
      'Expected delivery time in free text. For example, `"3-5 business days"`.'
    )
    .max(500)
    .optional()
    .nullable(),
  instructions: z
    .string()
    .describe(
      'Instructions for carrier. For example, `"Please knock on the door. If unanswered, please call contact number. Thanks."`.'
    )
    .max(1000)
    .optional()
    .nullable(),
  active: z
    .boolean()
    .describe('inactive pickup locations should not be shown in checkout')
    .optional()
    .nullable(),
  rates: z
    .array(
      z.object({
        amount: z
          .string()
          .describe(
            'there is an AND logic between all the conditions. Empty conditions means true.\nThe amount of the rate that will be returned if all conditions are met.'
          )
          .optional(),
        conditions: z
          .array(
            z.object({
              type: z
                .enum([
                  'BY_TOTAL_WEIGHT',
                  'BY_TOTAL_PRICE',
                  'BY_TOTAL_QUANTITY',
                ])
                .optional(),
              value: z
                .string()
                .describe(
                  'The value in respective to the condition type\nWeight values should be in the same weight units of the store: KG / LB\nTotal price is according to the store currency\nQuantity of items should be integers'
                )
                .optional(),
              operator: z
                .enum(['EQ', 'GT', 'GTE', 'LT', 'LTE'])
                .describe('Logical operator.')
                .optional(),
            })
          )
          .min(0)
          .max(10)
          .optional(),
        multiplyByQuantity: z
          .boolean()
          .describe(
            'When this flag is set to true, multiply the amount by the number of line items passed on the request.'
          )
          .optional(),
      })
    )
    .max(50)
    .optional(),
  deliveryRegionIds: z.array(z.string()).max(50).optional(),
});
export const UpdatePickupLocationRequest = z.object({
  _id: z
    .string()
    .describe('PickupLocation ID')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  pickupLocation: z
    .object({
      _id: z
        .string()
        .describe('PickupLocation ID')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision'
        ),
      _createdDate: z
        .date()
        .describe('Represents the time this PickupLocation was created')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Represents the time this PickupLocation was last updated')
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Pickup Location Name')
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      address: z
        .object({
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
          addressLine1: z.string().optional().nullable(),
          addressLine2: z.string().optional().nullable(),
        })
        .describe('Pickup Location Address')
        .optional(),
      deliveryTime: z
        .string()
        .describe(
          'Expected delivery time in free text. For example, `"3-5 business days"`.'
        )
        .max(500)
        .optional()
        .nullable(),
      instructions: z
        .string()
        .describe(
          'Instructions for carrier. For example, `"Please knock on the door. If unanswered, please call contact number. Thanks."`.'
        )
        .max(1000)
        .optional()
        .nullable(),
      active: z
        .boolean()
        .describe('inactive pickup locations should not be shown in checkout')
        .optional()
        .nullable(),
      rates: z
        .array(
          z.object({
            amount: z
              .string()
              .describe(
                'there is an AND logic between all the conditions. Empty conditions means true.\nThe amount of the rate that will be returned if all conditions are met.'
              )
              .optional(),
            conditions: z
              .array(
                z.object({
                  type: z
                    .enum([
                      'BY_TOTAL_WEIGHT',
                      'BY_TOTAL_PRICE',
                      'BY_TOTAL_QUANTITY',
                    ])
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'The value in respective to the condition type\nWeight values should be in the same weight units of the store: KG / LB\nTotal price is according to the store currency\nQuantity of items should be integers'
                    )
                    .optional(),
                  operator: z.enum(['EQ', 'GT', 'GTE', 'LT', 'LTE']).optional(),
                })
              )
              .min(0)
              .max(10)
              .optional(),
            multiplyByQuantity: z
              .boolean()
              .describe(
                'When this flag is set to true, multiply the amount by the number of line items passed on the request.'
              )
              .optional(),
          })
        )
        .max(50)
        .optional(),
      deliveryRegionIds: z.array(z.string()).max(50).optional(),
    })
    .describe('PickupLocation to be updated, may be partial'),
});
export const UpdatePickupLocationResponse = z.object({
  _id: z
    .string()
    .describe('PickupLocation ID')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Represents the time this PickupLocation was created')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Represents the time this PickupLocation was last updated')
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Pickup Location Name')
    .min(1)
    .max(100)
    .optional()
    .nullable(),
  address: z
    .object({
      city: z.string().optional().nullable(),
      subdivision: z.string().optional().nullable(),
      country: z.string().optional().nullable(),
      postalCode: z.string().optional().nullable(),
      addressLine1: z.string().optional().nullable(),
      addressLine2: z.string().optional().nullable(),
    })
    .describe('Pickup Location Address')
    .optional(),
  deliveryTime: z
    .string()
    .describe(
      'Expected delivery time in free text. For example, `"3-5 business days"`.'
    )
    .max(500)
    .optional()
    .nullable(),
  instructions: z
    .string()
    .describe(
      'Instructions for carrier. For example, `"Please knock on the door. If unanswered, please call contact number. Thanks."`.'
    )
    .max(1000)
    .optional()
    .nullable(),
  active: z
    .boolean()
    .describe('inactive pickup locations should not be shown in checkout')
    .optional()
    .nullable(),
  rates: z
    .array(
      z.object({
        amount: z
          .string()
          .describe(
            'there is an AND logic between all the conditions. Empty conditions means true.\nThe amount of the rate that will be returned if all conditions are met.'
          )
          .optional(),
        conditions: z
          .array(
            z.object({
              type: z
                .enum([
                  'BY_TOTAL_WEIGHT',
                  'BY_TOTAL_PRICE',
                  'BY_TOTAL_QUANTITY',
                ])
                .optional(),
              value: z
                .string()
                .describe(
                  'The value in respective to the condition type\nWeight values should be in the same weight units of the store: KG / LB\nTotal price is according to the store currency\nQuantity of items should be integers'
                )
                .optional(),
              operator: z
                .enum(['EQ', 'GT', 'GTE', 'LT', 'LTE'])
                .describe('Logical operator.')
                .optional(),
            })
          )
          .min(0)
          .max(10)
          .optional(),
        multiplyByQuantity: z
          .boolean()
          .describe(
            'When this flag is set to true, multiply the amount by the number of line items passed on the request.'
          )
          .optional(),
      })
    )
    .max(50)
    .optional(),
  deliveryRegionIds: z.array(z.string()).max(50).optional(),
});
export const DeletePickupLocationRequest = z.object({
  pickupLocationId: z
    .string()
    .describe('Id of the PickupLocation to delete')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeletePickupLocationResponse = z.object({});
export const QueryPickupLocationRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .optional(),
      }),
      z.xor([
        z.object({
          paging: z.never().optional(),
          cursorPaging: z.never().optional(),
        }),
        z.object({
          cursorPaging: z.never().optional(),
          paging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Number of items to load.')
                .min(0)
                .optional()
                .nullable(),
              offset: z
                .number()
                .int()
                .describe('Number of items to skip in the current sort order.')
                .min(0)
                .optional()
                .nullable(),
            })
            .describe(
              'Paging options to limit and offset the number of items.'
            ),
        }),
        z.object({
          paging: z.never().optional(),
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe('WQL expression'),
});
export const QueryPickupLocationResponse = z.object({
  pickupLocations: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('PickupLocation ID')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Represents the time this PickupLocation was created')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Represents the time this PickupLocation was last updated')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Pickup Location Name')
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        address: z
          .object({
            city: z.string().optional().nullable(),
            subdivision: z.string().optional().nullable(),
            country: z.string().optional().nullable(),
            postalCode: z.string().optional().nullable(),
            addressLine1: z.string().optional().nullable(),
            addressLine2: z.string().optional().nullable(),
          })
          .describe('Pickup Location Address')
          .optional(),
        deliveryTime: z
          .string()
          .describe(
            'Expected delivery time in free text. For example, `"3-5 business days"`.'
          )
          .max(500)
          .optional()
          .nullable(),
        instructions: z
          .string()
          .describe(
            'Instructions for carrier. For example, `"Please knock on the door. If unanswered, please call contact number. Thanks."`.'
          )
          .max(1000)
          .optional()
          .nullable(),
        active: z
          .boolean()
          .describe('inactive pickup locations should not be shown in checkout')
          .optional()
          .nullable(),
        rates: z
          .array(
            z.object({
              amount: z
                .string()
                .describe(
                  'there is an AND logic between all the conditions. Empty conditions means true.\nThe amount of the rate that will be returned if all conditions are met.'
                )
                .optional(),
              conditions: z
                .array(
                  z.object({
                    type: z
                      .enum([
                        'BY_TOTAL_WEIGHT',
                        'BY_TOTAL_PRICE',
                        'BY_TOTAL_QUANTITY',
                      ])
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'The value in respective to the condition type\nWeight values should be in the same weight units of the store: KG / LB\nTotal price is according to the store currency\nQuantity of items should be integers'
                      )
                      .optional(),
                    operator: z
                      .enum(['EQ', 'GT', 'GTE', 'LT', 'LTE'])
                      .describe('Logical operator.')
                      .optional(),
                  })
                )
                .min(0)
                .max(10)
                .optional(),
              multiplyByQuantity: z
                .boolean()
                .describe(
                  'When this flag is set to true, multiply the amount by the number of line items passed on the request.'
                )
                .optional(),
            })
          )
          .max(50)
          .optional(),
        deliveryRegionIds: z.array(z.string()).max(50).optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata')
    .optional(),
});
export const AddDeliveryRegionRequest = z.object({
  pickupLocationId: z
    .string()
    .describe('Id of the PickupLocation to add to the delivery region')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  deliveryRegionId: z
    .string()
    .describe('Id of the DeliveryRegion to add the PickupLocation to')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe('Revision of the PickupLocation'),
  }),
});
export const AddDeliveryRegionResponse = z.object({
  pickupLocation: z
    .object({
      _id: z
        .string()
        .describe('PickupLocation ID')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Represents the time this PickupLocation was created')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Represents the time this PickupLocation was last updated')
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Pickup Location Name')
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      address: z
        .object({
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
          addressLine1: z.string().optional().nullable(),
          addressLine2: z.string().optional().nullable(),
        })
        .describe('Pickup Location Address')
        .optional(),
      deliveryTime: z
        .string()
        .describe(
          'Expected delivery time in free text. For example, `"3-5 business days"`.'
        )
        .max(500)
        .optional()
        .nullable(),
      instructions: z
        .string()
        .describe(
          'Instructions for carrier. For example, `"Please knock on the door. If unanswered, please call contact number. Thanks."`.'
        )
        .max(1000)
        .optional()
        .nullable(),
      active: z
        .boolean()
        .describe('inactive pickup locations should not be shown in checkout')
        .optional()
        .nullable(),
      rates: z
        .array(
          z.object({
            amount: z
              .string()
              .describe(
                'there is an AND logic between all the conditions. Empty conditions means true.\nThe amount of the rate that will be returned if all conditions are met.'
              )
              .optional(),
            conditions: z
              .array(
                z.object({
                  type: z
                    .enum([
                      'BY_TOTAL_WEIGHT',
                      'BY_TOTAL_PRICE',
                      'BY_TOTAL_QUANTITY',
                    ])
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'The value in respective to the condition type\nWeight values should be in the same weight units of the store: KG / LB\nTotal price is according to the store currency\nQuantity of items should be integers'
                    )
                    .optional(),
                  operator: z
                    .enum(['EQ', 'GT', 'GTE', 'LT', 'LTE'])
                    .describe('Logical operator.')
                    .optional(),
                })
              )
              .min(0)
              .max(10)
              .optional(),
            multiplyByQuantity: z
              .boolean()
              .describe(
                'When this flag is set to true, multiply the amount by the number of line items passed on the request.'
              )
              .optional(),
          })
        )
        .max(50)
        .optional(),
      deliveryRegionIds: z.array(z.string()).max(50).optional(),
    })
    .describe('The updated PickupLocation')
    .optional(),
});
export const RemoveDeliveryRegionRequest = z.object({
  pickupLocationId: z
    .string()
    .describe('Id of the PickupLocation to add to the delivery region')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  deliveryRegionId: z
    .string()
    .describe('Id of the DeliveryRegion to add the PickupLocation to')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe('Revision of the PickupLocation'),
  }),
});
export const RemoveDeliveryRegionResponse = z.object({
  pickupLocation: z
    .object({
      _id: z
        .string()
        .describe('PickupLocation ID')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Represents the time this PickupLocation was created')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Represents the time this PickupLocation was last updated')
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Pickup Location Name')
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      address: z
        .object({
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
          addressLine1: z.string().optional().nullable(),
          addressLine2: z.string().optional().nullable(),
        })
        .describe('Pickup Location Address')
        .optional(),
      deliveryTime: z
        .string()
        .describe(
          'Expected delivery time in free text. For example, `"3-5 business days"`.'
        )
        .max(500)
        .optional()
        .nullable(),
      instructions: z
        .string()
        .describe(
          'Instructions for carrier. For example, `"Please knock on the door. If unanswered, please call contact number. Thanks."`.'
        )
        .max(1000)
        .optional()
        .nullable(),
      active: z
        .boolean()
        .describe('inactive pickup locations should not be shown in checkout')
        .optional()
        .nullable(),
      rates: z
        .array(
          z.object({
            amount: z
              .string()
              .describe(
                'there is an AND logic between all the conditions. Empty conditions means true.\nThe amount of the rate that will be returned if all conditions are met.'
              )
              .optional(),
            conditions: z
              .array(
                z.object({
                  type: z
                    .enum([
                      'BY_TOTAL_WEIGHT',
                      'BY_TOTAL_PRICE',
                      'BY_TOTAL_QUANTITY',
                    ])
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'The value in respective to the condition type\nWeight values should be in the same weight units of the store: KG / LB\nTotal price is according to the store currency\nQuantity of items should be integers'
                    )
                    .optional(),
                  operator: z
                    .enum(['EQ', 'GT', 'GTE', 'LT', 'LTE'])
                    .describe('Logical operator.')
                    .optional(),
                })
              )
              .min(0)
              .max(10)
              .optional(),
            multiplyByQuantity: z
              .boolean()
              .describe(
                'When this flag is set to true, multiply the amount by the number of line items passed on the request.'
              )
              .optional(),
          })
        )
        .max(50)
        .optional(),
      deliveryRegionIds: z.array(z.string()).max(50).optional(),
    })
    .describe('The updated PickupLocation')
    .optional(),
});
export const BulkCreatePickupLocationRequest = z.object({
  pickupLocations: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('PickupLocation ID')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Represents the time this PickupLocation was created')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Represents the time this PickupLocation was last updated')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Pickup Location Name')
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        address: z
          .object({
            city: z.string().optional().nullable(),
            subdivision: z.string().optional().nullable(),
            country: z.string().optional().nullable(),
            postalCode: z.string().optional().nullable(),
            addressLine1: z.string().optional().nullable(),
            addressLine2: z.string().optional().nullable(),
          })
          .describe('Pickup Location Address'),
        deliveryTime: z
          .string()
          .describe(
            'Expected delivery time in free text. For example, `"3-5 business days"`.'
          )
          .max(500)
          .optional()
          .nullable(),
        instructions: z
          .string()
          .describe(
            'Instructions for carrier. For example, `"Please knock on the door. If unanswered, please call contact number. Thanks."`.'
          )
          .max(1000)
          .optional()
          .nullable(),
        active: z
          .boolean()
          .describe('inactive pickup locations should not be shown in checkout')
          .optional()
          .nullable(),
        rates: z
          .array(
            z.object({
              amount: z
                .string()
                .describe(
                  'there is an AND logic between all the conditions. Empty conditions means true.\nThe amount of the rate that will be returned if all conditions are met.'
                )
                .optional(),
              conditions: z
                .array(
                  z.object({
                    type: z
                      .enum([
                        'BY_TOTAL_WEIGHT',
                        'BY_TOTAL_PRICE',
                        'BY_TOTAL_QUANTITY',
                      ])
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'The value in respective to the condition type\nWeight values should be in the same weight units of the store: KG / LB\nTotal price is according to the store currency\nQuantity of items should be integers'
                      )
                      .optional(),
                    operator: z
                      .enum(['EQ', 'GT', 'GTE', 'LT', 'LTE'])
                      .optional(),
                  })
                )
                .min(0)
                .max(10)
                .optional(),
              multiplyByQuantity: z
                .boolean()
                .describe(
                  'When this flag is set to true, multiply the amount by the number of line items passed on the request.'
                )
                .optional(),
            })
          )
          .max(50)
          .optional(),
        deliveryRegionIds: z.array(z.string()).max(50).optional(),
      })
    )
    .min(1)
    .max(100),
});
export const BulkCreatePickupLocationResponse = z.object({
  pickupLocations: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('PickupLocation ID')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Represents the time this PickupLocation was created')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Represents the time this PickupLocation was last updated')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Pickup Location Name')
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        address: z
          .object({
            city: z.string().optional().nullable(),
            subdivision: z.string().optional().nullable(),
            country: z.string().optional().nullable(),
            postalCode: z.string().optional().nullable(),
            addressLine1: z.string().optional().nullable(),
            addressLine2: z.string().optional().nullable(),
          })
          .describe('Pickup Location Address')
          .optional(),
        deliveryTime: z
          .string()
          .describe(
            'Expected delivery time in free text. For example, `"3-5 business days"`.'
          )
          .max(500)
          .optional()
          .nullable(),
        instructions: z
          .string()
          .describe(
            'Instructions for carrier. For example, `"Please knock on the door. If unanswered, please call contact number. Thanks."`.'
          )
          .max(1000)
          .optional()
          .nullable(),
        active: z
          .boolean()
          .describe('inactive pickup locations should not be shown in checkout')
          .optional()
          .nullable(),
        rates: z
          .array(
            z.object({
              amount: z
                .string()
                .describe(
                  'there is an AND logic between all the conditions. Empty conditions means true.\nThe amount of the rate that will be returned if all conditions are met.'
                )
                .optional(),
              conditions: z
                .array(
                  z.object({
                    type: z
                      .enum([
                        'BY_TOTAL_WEIGHT',
                        'BY_TOTAL_PRICE',
                        'BY_TOTAL_QUANTITY',
                      ])
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'The value in respective to the condition type\nWeight values should be in the same weight units of the store: KG / LB\nTotal price is according to the store currency\nQuantity of items should be integers'
                      )
                      .optional(),
                    operator: z
                      .enum(['EQ', 'GT', 'GTE', 'LT', 'LTE'])
                      .describe('Logical operator.')
                      .optional(),
                  })
                )
                .min(0)
                .max(10)
                .optional(),
              multiplyByQuantity: z
                .boolean()
                .describe(
                  'When this flag is set to true, multiply the amount by the number of line items passed on the request.'
                )
                .optional(),
            })
          )
          .max(50)
          .optional(),
        deliveryRegionIds: z.array(z.string()).max(50).optional(),
      })
    )
    .max(100)
    .optional(),
  errors: z
    .array(
      z.object({
        _id: z.string().optional(),
        error: z
          .object({
            code: z.string().describe('Error code.').optional(),
            description: z
              .string()
              .describe('Description of the error.')
              .optional(),
            data: z
              .record(z.string(), z.any())
              .describe('Data related to the error.')
              .optional()
              .nullable(),
          })
          .optional(),
      })
    )
    .optional(),
});
export const BulkUpdatePickupLocationRequest = z.object({
  pickupLocations: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('PickupLocation ID')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision'
          ),
        _createdDate: z
          .date()
          .describe('Represents the time this PickupLocation was created')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Represents the time this PickupLocation was last updated')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Pickup Location Name')
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        address: z
          .object({
            city: z.string().optional().nullable(),
            subdivision: z.string().optional().nullable(),
            country: z.string().optional().nullable(),
            postalCode: z.string().optional().nullable(),
            addressLine1: z.string().optional().nullable(),
            addressLine2: z.string().optional().nullable(),
          })
          .describe('Pickup Location Address')
          .optional(),
        deliveryTime: z
          .string()
          .describe(
            'Expected delivery time in free text. For example, `"3-5 business days"`.'
          )
          .max(500)
          .optional()
          .nullable(),
        instructions: z
          .string()
          .describe(
            'Instructions for carrier. For example, `"Please knock on the door. If unanswered, please call contact number. Thanks."`.'
          )
          .max(1000)
          .optional()
          .nullable(),
        active: z
          .boolean()
          .describe('inactive pickup locations should not be shown in checkout')
          .optional()
          .nullable(),
        rates: z
          .array(
            z.object({
              amount: z
                .string()
                .describe(
                  'there is an AND logic between all the conditions. Empty conditions means true.\nThe amount of the rate that will be returned if all conditions are met.'
                )
                .optional(),
              conditions: z
                .array(
                  z.object({
                    type: z
                      .enum([
                        'BY_TOTAL_WEIGHT',
                        'BY_TOTAL_PRICE',
                        'BY_TOTAL_QUANTITY',
                      ])
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'The value in respective to the condition type\nWeight values should be in the same weight units of the store: KG / LB\nTotal price is according to the store currency\nQuantity of items should be integers'
                      )
                      .optional(),
                    operator: z
                      .enum(['EQ', 'GT', 'GTE', 'LT', 'LTE'])
                      .optional(),
                  })
                )
                .min(0)
                .max(10)
                .optional(),
              multiplyByQuantity: z
                .boolean()
                .describe(
                  'When this flag is set to true, multiply the amount by the number of line items passed on the request.'
                )
                .optional(),
            })
          )
          .max(50)
          .optional(),
        deliveryRegionIds: z.array(z.string()).max(50).optional(),
      })
    )
    .min(1)
    .max(100),
});
export const BulkUpdatePickupLocationResponse = z.object({
  pickupLocations: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('PickupLocation ID')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Represents the time this PickupLocation was created')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Represents the time this PickupLocation was last updated')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Pickup Location Name')
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        address: z
          .object({
            city: z.string().optional().nullable(),
            subdivision: z.string().optional().nullable(),
            country: z.string().optional().nullable(),
            postalCode: z.string().optional().nullable(),
            addressLine1: z.string().optional().nullable(),
            addressLine2: z.string().optional().nullable(),
          })
          .describe('Pickup Location Address')
          .optional(),
        deliveryTime: z
          .string()
          .describe(
            'Expected delivery time in free text. For example, `"3-5 business days"`.'
          )
          .max(500)
          .optional()
          .nullable(),
        instructions: z
          .string()
          .describe(
            'Instructions for carrier. For example, `"Please knock on the door. If unanswered, please call contact number. Thanks."`.'
          )
          .max(1000)
          .optional()
          .nullable(),
        active: z
          .boolean()
          .describe('inactive pickup locations should not be shown in checkout')
          .optional()
          .nullable(),
        rates: z
          .array(
            z.object({
              amount: z
                .string()
                .describe(
                  'there is an AND logic between all the conditions. Empty conditions means true.\nThe amount of the rate that will be returned if all conditions are met.'
                )
                .optional(),
              conditions: z
                .array(
                  z.object({
                    type: z
                      .enum([
                        'BY_TOTAL_WEIGHT',
                        'BY_TOTAL_PRICE',
                        'BY_TOTAL_QUANTITY',
                      ])
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'The value in respective to the condition type\nWeight values should be in the same weight units of the store: KG / LB\nTotal price is according to the store currency\nQuantity of items should be integers'
                      )
                      .optional(),
                    operator: z
                      .enum(['EQ', 'GT', 'GTE', 'LT', 'LTE'])
                      .describe('Logical operator.')
                      .optional(),
                  })
                )
                .min(0)
                .max(10)
                .optional(),
              multiplyByQuantity: z
                .boolean()
                .describe(
                  'When this flag is set to true, multiply the amount by the number of line items passed on the request.'
                )
                .optional(),
            })
          )
          .max(50)
          .optional(),
        deliveryRegionIds: z.array(z.string()).max(50).optional(),
      })
    )
    .max(100)
    .optional(),
  errors: z
    .array(
      z.object({
        _id: z.string().optional(),
        error: z
          .object({
            code: z.string().describe('Error code.').optional(),
            description: z
              .string()
              .describe('Description of the error.')
              .optional(),
            data: z
              .record(z.string(), z.any())
              .describe('Data related to the error.')
              .optional()
              .nullable(),
          })
          .optional(),
      })
    )
    .optional(),
});
export const BulkDeletePickupLocationRequest = z.object({
  pickupLocationIds: z.array(z.string()).min(1).max(100),
});
export const BulkDeletePickupLocationResponse = z.object({
  errors: z
    .array(
      z.object({
        _id: z.string().optional(),
        error: z
          .object({
            code: z.string().describe('Error code.').optional(),
            description: z
              .string()
              .describe('Description of the error.')
              .optional(),
            data: z
              .record(z.string(), z.any())
              .describe('Data related to the error.')
              .optional()
              .nullable(),
          })
          .optional(),
      })
    )
    .optional(),
});
